import { create } from 'zustand'

import { api, ErrorApi, fijarAutorizador } from '@/lib/api'

/** Lo que se está pidiendo autorizar. */
export interface Pedido {
  /** La clave del permiso, que es lo que viaja al servidor. */
  permiso: string

  /** El permiso en castellano («Anular ventas»), que es lo que se muestra. */
  etiqueta: string

  responder: (grant: string | null) => void
}

interface Estado {
  pedido: Pedido | null
  pedir: (p: Pedido) => void
  responder: (grant: string | null) => void
}

/**
 * La autorización de un encargado con su PIN.
 *
 * §1.4 dice que los permisos son granulares porque en una verdulería esto es
 * control de fuga de efectivo, y la mitad que hacía falta para que eso
 * funcione de verdad es ESTA: si al cajero le falta un permiso y la única
 * salida es que el dueño se lo agregue al rol, en tres días todos los cajeros
 * tienen todos los permisos y el control dejó de existir.
 *
 * El servidor lo tenía completo desde el principio —`POST /api/auth/elevar`,
 * el grant de 60 segundos, el middleware que lo lee, la anotación en la
 * bitácora, el freno del PIN, el índice ciego para encontrar de quién es— y
 * hasta el mensaje del 403 lo prometía: «Un encargado puede autorizarlo con su
 * PIN». Lo que no existía era la pantalla. Nadie llamaba a `elevar` y ningún
 * lugar mandaba el header, así que el mensaje prometía algo que no se podía
 * hacer. Es el mismo error que la tecla rápida del medio de pago, en grande.
 */
export const useElevacion = create<Estado>((set, get) => ({
  pedido: null,

  pedir: (p) => {
    // Igual que en la confirmación: al pisar un pedido abierto hay que
    // responderle, o el `await` del que lo pidió no vuelve nunca.
    get().pedido?.responder(null)
    set({ pedido: p })
  },

  responder: (grant) => {
    const { pedido } = get()
    if (!pedido) return

    set({ pedido: null })
    pedido.responder(grant)
  },
}))

/** Uno de los que podrían autorizar con ese PIN. */
export interface Candidato {
  id: number
  nombre: string
}

/**
 * Canjea el PIN por el grant, o por la pregunta de quién autoriza.
 *
 * Vive acá y no en el componente para que el diálogo no sepa de rutas. El
 * mensaje de error lo escribe el servidor y se muestra tal cual: un PIN que no
 * es de nadie y el PIN de alguien que tampoco tiene ese permiso son dos cosas
 * distintas, y la segunda es la que le dice al cajero a quién ir a buscar.
 *
 * Cuando vuelven `candidatos` en lugar del grant, ese PIN es de VARIAS
 * personas que además podrían autorizar esto. No es un error —el PIN está
 * bien— y por eso viene con 200: lo que falta es que alguien diga cuál es. El
 * PIN dejó de ser único a propósito (para entrar se elige la persona y después
 * se pone el PIN, así que dos empleados con el mismo PIN no se pisan) y esto
 * es lo único que la unicidad resolvía.
 */
export async function canjearPin(
  pin: string,
  permiso: string,
  usuarioId?: number,
): Promise<{ grant?: string; candidatos?: Candidato[] }> {
  const r = await api.post<{ grant?: string; candidatos?: Candidato[] }>(
    '/auth/elevar',
    { pin, permiso, usuarioId },
  )

  return r.grant ? { grant: r.grant } : { candidatos: r.candidatos ?? [] }
}

/**
 * Conecta el mecanismo al cliente de la API. Devuelve cómo desconectarlo.
 *
 * Lo llama el propio `DialogoElevacion` al montarse, por lo mismo que el atajo
 * F1 vive en el cajón de ayuda: así el mecanismo está disponible exactamente
 * donde hay un diálogo que lo atienda. Sin el diálogo montado —el panel de
 * plataforma, que no tiene permisos de local— un 403 sigue siendo un 403.
 */
export function conectarAutorizadorDePin(): () => void {
  fijarAutorizador(
    (permiso, etiqueta) =>
      new Promise<string | null>((resolver) => {
        useElevacion.getState().pedir({
          permiso,

          // Si el servidor no mandó etiqueta —un permiso que no está en el
          // catálogo— se muestra la clave: es fea pero es cierta, y deja al
          // cajero pedir exactamente eso.
          etiqueta: etiqueta ?? permiso,
          responder: resolver,
        })
      }),
  )

  return () => fijarAutorizador(null)
}

/** Si el error es una falta de permiso que un encargado podría autorizar. */
export function sePuedeAutorizar(e: unknown): boolean {
  return e instanceof ErrorApi && e.esFaltaDePermiso
}
